import { useEffect, useState } from 'react';
import { disclaimerAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Save, ShieldAlert, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/utils';
import { DEFAULT_DISCLAIMER } from '@/components/common/disclaimerContent';

/**
 * DisclaimerConfigPage — KR SuperAdmin surface to publish a new
 * disclaimer version. Bumping the version invalidates every previous
 * acceptance so every agent has to re-accept on next login.
 *
 * Route (see App.js): /admin/disclaimer
 */
export default function DisclaimerConfigPage() {
  const [loading, setLoading] = useState(true);
  const [cfg, setCfg] = useState(null);
  const [newVersion, setNewVersion] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  // Audit report state
  const [auditDays, setAuditDays] = useState(30);
  const [auditQ, setAuditQ] = useState('');
  const [audit, setAudit] = useState(null);
  const [loadingAudit, setLoadingAudit] = useState(false);
  // Editable disclaimer text (per language).
  const [editLang, setEditLang] = useState('en');
  const [content, setContent] = useState({
    title_en: '', title_es: '', body_en: '', body_es: '',
  });
  const [savingContent, setSavingContent] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await disclaimerAPI.getConfig();
      setCfg(r.data);
      setNewVersion(suggestNext(r.data.version));
      setContent({
        title_en: r.data.title_en || DEFAULT_DISCLAIMER.en.title,
        title_es: r.data.title_es || DEFAULT_DISCLAIMER.es.title,
        body_en: r.data.body_en || DEFAULT_DISCLAIMER.en.body,
        body_es: r.data.body_es || DEFAULT_DISCLAIMER.es.body,
      });
    } catch (e) { toast.error(getErrorMessage(e, 'Failed to load config')); }
    finally { setLoading(false); }
  };
  const loadAudit = async () => {
    setLoadingAudit(true);
    try {
      const r = await disclaimerAPI.listAcceptances({
        days: auditDays,
        q: auditQ.trim() || undefined,
        limit: 500,
      });
      setAudit(r.data);
    } catch (e) { toast.error(getErrorMessage(e, 'Failed to load audit')); }
    finally { setLoadingAudit(false); }
  };
  useEffect(() => { load(); loadAudit(); }, []);

  const saveText = async () => {
    setSavingContent(true);
    try {
      await disclaimerAPI.saveContent(content);
      toast.success('Disclaimer text saved (not yet published as a new version)');
      await load();
    } catch (e) { toast.error(getErrorMessage(e, 'Save failed')); }
    finally { setSavingContent(false); }
  };

  const submit = async () => {
    const v = newVersion.trim();
    if (!v) { toast.error('Enter a new version'); return; }
    if (v === cfg?.version) { toast.error('Version is the same as the current one'); return; }
    if (!window.confirm(
      `Publish disclaimer version ${v} with the current text? Every gated agent/agency will be forced to re-accept on their next login.`
    )) return;
    setSaving(true);
    try {
      const r = await disclaimerAPI.bumpVersion(v, reason.trim() || undefined, content);
      toast.success(`Published v${r.data.version} (was v${r.data.previous_version})`);
      setReason('');
      await load();
    } catch (e) { toast.error(getErrorMessage(e, 'Bump failed')); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-6 text-slate-500">Loading…</div>;
  if (!cfg) return null;

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="admin-disclaimer-page">
      <div className="flex items-center gap-3">
        <ShieldAlert className="text-amber-600" size={22} />
        <h1 className="text-2xl font-bold text-slate-900">Disclaimer Version</h1>
      </div>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Current published version</CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-1.5">
          <div><span className="text-slate-500">Version:</span> <span className="font-mono font-semibold" data-testid="current-version">{cfg.version}</span></div>
          {cfg.updated_at && (
            <div><span className="text-slate-500">Updated:</span> {new Date(cfg.updated_at).toLocaleString()}</div>
          )}
          {cfg.updated_by && (
            <div><span className="text-slate-500">By:</span> {cfg.updated_by}</div>
          )}
          <div>
            <span className="text-slate-500">Acceptances at this version:</span>{' '}
            <span className="font-semibold">{cfg.acceptances_at_current_version}</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText size={16} className="text-slate-500" />
            Disclaimer text
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-slate-600">
            Edit the confidentiality text agents see. Use <span className="font-mono">**bold**</span> for
            emphasis, a blank line to start a new paragraph, and lines starting
            with <span className="font-mono">- </span> for bullet points. Editing here and clicking
            <strong> Save text</strong> updates the wording without forcing anyone to re-accept.
            To make everyone re-accept, publish a new version below (it saves this text too).
          </p>
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-sm">
            <button
              type="button"
              onClick={() => setEditLang('en')}
              data-testid="edit-lang-en"
              className={`px-4 py-1.5 font-medium ${editLang === 'en' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600'}`}
            >English</button>
            <button
              type="button"
              onClick={() => setEditLang('es')}
              data-testid="edit-lang-es"
              className={`px-4 py-1.5 font-medium ${editLang === 'es' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600'}`}
            >Español</button>
          </div>
          {editLang === 'en' ? (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Title (English)</Label>
                <Input
                  value={content.title_en}
                  onChange={(e) => setContent((c) => ({ ...c, title_en: e.target.value }))}
                  data-testid="disclaimer-title-en"
                />
              </div>
              <div>
                <Label className="text-xs">Body (English)</Label>
                <Textarea
                  value={content.body_en}
                  onChange={(e) => setContent((c) => ({ ...c, body_en: e.target.value }))}
                  rows={14}
                  className="font-mono text-xs leading-relaxed"
                  data-testid="disclaimer-body-en"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Título (Español)</Label>
                <Input
                  value={content.title_es}
                  onChange={(e) => setContent((c) => ({ ...c, title_es: e.target.value }))}
                  data-testid="disclaimer-title-es"
                />
              </div>
              <div>
                <Label className="text-xs">Texto (Español)</Label>
                <Textarea
                  value={content.body_es}
                  onChange={(e) => setContent((c) => ({ ...c, body_es: e.target.value }))}
                  rows={14}
                  className="font-mono text-xs leading-relaxed"
                  data-testid="disclaimer-body-es"
                />
              </div>
            </div>
          )}
          <div className="flex justify-end">
            <Button
              onClick={saveText}
              disabled={savingContent}
              variant="outline"
              data-testid="save-text-btn"
            >
              {savingContent ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />}
              Save text
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Publish a new version</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-slate-600">
            Bumping the version invalidates every previous acceptance and
            forces every gated agent/agency to re-accept the disclaimer on
            their next login. Use it whenever the legal text materially
            changes.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">New version</Label>
              <Input
                value={newVersion}
                onChange={(e) => setNewVersion(e.target.value)}
                placeholder={suggestNext(cfg.version)}
                className="font-mono"
                data-testid="new-version-input"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Free-form. Suggested: {suggestNext(cfg.version)}.
              </p>
            </div>
            <div>
              <Label className="text-xs">Reason (optional)</Label>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Added Sora video usage clause"
                data-testid="reason-input"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              onClick={submit}
              disabled={saving || !newVersion.trim() || newVersion.trim() === cfg.version}
              data-testid="publish-version-btn"
              className="bg-slate-900 hover:bg-slate-800 text-white"
            >
              {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />}
              Publish version
            </Button>
          </div>
        </CardContent>
      </Card>

      {cfg.history?.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Recent version history</CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-xs" data-testid="version-history">
              <thead className="text-slate-500">
                <tr><th className="text-left py-1">From → To</th><th className="text-left py-1">By</th><th className="text-left py-1">When</th><th className="text-left py-1">Reason</th></tr>
              </thead>
              <tbody>
                {cfg.history.map((h, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="py-1 font-mono">{h.from_version} → {h.to_version}</td>
                    <td className="py-1">{h.bumped_by || '—'}</td>
                    <td className="py-1">{h.bumped_at ? new Date(h.bumped_at).toLocaleString() : '—'}</td>
                    <td className="py-1 text-slate-600">{h.reason || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Acceptance audit report</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs">Days</Label>
              <Input
                type="number" min="1" max="365"
                value={auditDays}
                onChange={(e) => setAuditDays(parseInt(e.target.value || '30', 10) || 30)}
                className="h-9 w-24 text-sm"
                data-testid="audit-days-input"
              />
            </div>
            <div className="flex-1 min-w-[220px]">
              <Label className="text-xs">Email contains (optional)</Label>
              <Input
                value={auditQ}
                onChange={(e) => setAuditQ(e.target.value)}
                placeholder="rodrgz"
                className="h-9 text-sm"
                data-testid="audit-search-input"
              />
            </div>
            <Button
              variant="outline"
              onClick={loadAudit}
              disabled={loadingAudit}
              data-testid="audit-refresh-btn"
            >
              {loadingAudit ? <Loader2 size={13} className="mr-1 animate-spin" /> : null}
              Refresh
            </Button>
          </div>
          {audit && (
            <>
              <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-[11px] text-slate-700 grid grid-cols-3 gap-2" data-testid="audit-summary">
                <div><span className="text-slate-500">Rows</span>: <span className="font-semibold">{audit.count}</span></div>
                <div><span className="text-slate-500">Unique acceptors</span>: <span className="font-semibold">{audit.unique_acceptors}</span></div>
                <div><span className="text-slate-500">Total gated users</span>: <span className="font-semibold">{audit.total_gated_users}</span></div>
              </div>
              <div className="max-h-[400px] overflow-y-auto rounded border border-slate-200">
                <table className="w-full text-xs" data-testid="audit-table">
                  <thead className="sticky top-0 bg-slate-100 text-slate-600">
                    <tr>
                      <th className="text-left px-2 py-1">When</th>
                      <th className="text-left px-2 py-1">Email</th>
                      <th className="text-left px-2 py-1">Signed name</th>
                      <th className="text-left px-2 py-1">Version</th>
                      <th className="text-left px-2 py-1">IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {audit.items.length === 0 && (
                      <tr><td colSpan={5} className="px-2 py-4 text-center text-slate-500">No acceptances in this window.</td></tr>
                    )}
                    {audit.items.map((row, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-2 py-1 whitespace-nowrap">{new Date(row.accepted_at).toLocaleString()}</td>
                        <td className="px-2 py-1">{row.email || '—'}</td>
                        <td className="px-2 py-1">{row.signed_name || <span className="text-slate-400">—</span>}</td>
                        <td className="px-2 py-1 font-mono">{row.version || '—'}</td>
                        <td className="px-2 py-1 text-slate-500 font-mono">{row.ip || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function suggestNext(version) {
  const m = /^(\d+)(?:\.(\d+))?$/.exec(String(version || '').trim());
  if (!m) return '1.1';
  const major = Number(m[1] || 1);
  const minor = Number(m[2] || 0);
  return `${major}.${minor + 1}`;
}
